import {
    Component,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal
} from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ReportHeaderComponent } from "../../components/report-header/report-header.component";
import {
    EventChangeComparationParams,
    EventChangePeriodParams,
    ReportToolbarComponent
} from "../../components/report-toolbar/report-toolbar.component";
import { ReportVisualizationComponent } from "../../components/report-visualization/report-visualization.component";
import {
    NavigationItem,
    ReportAdjustmentsSidenavComponent
} from "../../components/report-adjustments-sidenav/report-adjustments-sidenav.component";
import { ReportFeedbackFormComponent } from "../../components/report-feedback-form/report-feedback-form.component";
import { ReportBeachmarkingSectionComponent } from "../../components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportPerformanceComponent } from "../../components/report-performance/report-performance.component";
import { DrawerService } from "@app/shared/services/drawer.service";
import {
    CompanyEntity,
    ReportEntity,
    ScenarioEntity
} from "@arch/domain/entity";
import { SelectOption } from "@app/shared/components/dsn/custom-select/custom-select.component";
import { MenuOption } from "@app/shared/components/dsn/button-menu-bubbles/button-menu-bubbles.component";
import { PeriodNames } from "@arch/_shared/types/interval-dates";
import { TableDreCompleteComponent } from "../../../../../shared/components/table-dre-complete/table-dre-complete.component";
import { TableBalanceComponent } from "../../../../../shared/components/table-balance/table-balance.component";
import { MenuBottomMobileComponent } from "@app/shared/components/menu-bottom-mobile/menu-bottom-mobile.component";
import { VisualizationSortService } from "@app/shared/services/visualization-sort.service";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ContactService } from "@app/shared/services/contact.service";
import { ReportPreferenceService } from "@arch/domain/application/services/report-preference.service";
@Component({
    selector: "app-mobile-dre",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ReportHeaderComponent,
        SharedMaterialModule,
        ReportHeaderComponent,
        ReportToolbarComponent,
        ReportVisualizationComponent,
        ReportAdjustmentsSidenavComponent,
        ReportFeedbackFormComponent,
        ReportBeachmarkingSectionComponent,
        ReportPerformanceComponent,
        TableDreCompleteComponent,
        TableBalanceComponent,
        MenuBottomMobileComponent
    ],
    templateUrl: "./mobile.component.html",
    styleUrl: "./mobile.component.scss"
})
export class MobileDreComponent {
    @Input({ required: true }) loading: boolean;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input() previewMode: boolean = false;
    @Input({ required: false }) set accordionState(value: string) {
        this.onListenerAccordion(value);
    }
    @Output() handleResetPeriod: EventEmitter<void> = new EventEmitter();
    @Output() handleChangePeriod: EventEmitter<EventChangePeriodParams> =
        new EventEmitter();
    @Output()
    handleChangeComparation: EventEmitter<EventChangeComparationParams> =
        new EventEmitter();
    @Output() handleChangeAnalysis: EventEmitter<{
        projection: ScenarioEntity;
        budget: ScenarioEntity;
        goals: boolean;
    }> = new EventEmitter();
    @Output() handleResetAnalysis: EventEmitter<void> = new EventEmitter();

    private readonly visualizationSortService = inject(
        VisualizationSortService
    );
    private readonly contactService = inject(ContactService);
    private readonly drawerService = inject(DrawerService);
    private readonly eventsService = inject(EventsService);
    public readonly preferenceService = inject(ReportPreferenceService);

    public contact = this.contactService.get();
    public drawerOpen: boolean = false;
    public initialPageSidenav: NavigationItem = null;
    public sectionStates: { [key: string]: boolean } = {
        SectionBenchmarking: true,
        SectionPerformance: true,
        SectionNavigationTips: true,
        SectionIndicators: true,
        SectionFeedback: true
    };
    public orderOptions = this.visualizationSortService.sortOptions;
    public selectedOrder: string = "Relevância";
    public hasBenchmarking: boolean = true;

    public bubblesMenuOptions: MenuOption[] = [
        {
            icon: "fas fa-bars",
            label: "Navegar",
            value: "navigation"
        },
        {
            icon: "fas fa-star",
            label: "Avaliar",
            value: "avaliar"
        },
        {
            icon: "fas fa-lightbulb",
            label: "Dicas",
            value: "dicas"
        }
    ];
    private pageInternalNavigation: NavigationItem = {
        label: "Navegação",
        icon: "fa-solid fa-bars",
        description: "Navegue pelas páginas do Dash.",
        page: 4
    };

    private readonly accordionStateSignal = signal<string>(this.accordionState);

    constructor() {
        this.drawerService.drawerState$.subscribe((state) => {
            this.drawerOpen = state;
            if (!state) {
                this.initialPageSidenav = null;
            }
        });

        effect(() => {
            const accordionState = this.accordionStateSignal();
            this.onListenerAccordion(accordionState);
        });
    }

    public onDrawerClosed(): void {
        this.drawerService.closeDrawer();
    }

    public onOptionSelected(event: {
        id: string;
        sectionId?: string;
        visualizationId?: string;
    }): void {
        this.drawerService.closeDrawer();
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[event.id] = true;
        if (event.visualizationId) {
            setTimeout(() => {
                const element = document.getElementById(event.visualizationId);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                }
            }, 100);
        }
        if (event.sectionId == "nanda") {
            this.eventsService.send(
                `Dash.${this.report.type}.${this.report.humanSourceName}.abrir-nanda`
            );
        }
    }

    public toggleSection(
        event: boolean,
        index?: number,
        sectionId?: string
    ): void {
        if (index !== undefined) {
            Object.keys(this.sectionStates).forEach((key) => {
                this.sectionStates[key] = false;
            });

            if (event) {
                this.sectionStates[sectionId] = true;
            }
        }
    }

    public onOrderChange(option: SelectOption): void {
        this.selectedOrder = option.label;
    }

    public get sortedVisualizations() {
        if (!this.report?.visualizations?.visualzations) {
            return [];
        }

        return this.visualizationSortService.sortVisualizations(
            this.report.visualizations.visualzations,
            this.selectedOrder
        );
    }

    public onSelectOptionBubbles(option: MenuOption): void {
        switch (option.value) {
            case "navigation":
                this.initialPageSidenav = this.pageInternalNavigation;
                this.drawerService.toggleDrawer();
                break;
            case "dicas":
                Object.keys(this.sectionStates).forEach((key) => {
                    this.sectionStates[key] = false;
                });
                this.sectionStates["SectionNavigationTips"] = true;
                setTimeout(() => {
                    const element = document.getElementById(
                        "SectionNavigationTips"
                    );
                    if (element) {
                        element.scrollIntoView({
                            behavior: "smooth",
                            block: "start"
                        });
                    }
                }, 100);
                break;
            case "avaliar":
                Object.keys(this.sectionStates).forEach((key) => {
                    this.sectionStates[key] = false;
                });
                this.sectionStates["SectionFeedback"] = true;
                setTimeout(() => {
                    const element = document.getElementById("SectionFeedback");
                    if (element) {
                        element.scrollIntoView({
                            behavior: "smooth",
                            block: "start"
                        });
                    }
                }, 100);
                break;
        }
    }

    private onListenerAccordion(accodionId: any): void {
        if (!accodionId) return;
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[accodionId] = true;
    }

    public onChangePeriod(form: {
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        startDate: Date;
        endDate: Date;
    }): void {
        this.drawerService.closeDrawer();
        this.handleChangePeriod.emit(form);
    }

    public onChangeComparation(form: {
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        dateBase: Date;
        comparationDate: Date;
        allMonthsCompare?: boolean;
    }): void {
        this.drawerService.closeDrawer();
        this.handleChangeComparation.emit(form);
    }

    public onResetPeriod(): void {
        this.drawerService.closeDrawer();
        this.handleResetPeriod.emit();
    }

    public onChangeAnalysis(analysis: {
        projection: ScenarioEntity;
        budget: ScenarioEntity;
        goals: boolean;
    }): void {
        this.drawerService.closeDrawer();
        this.handleChangeAnalysis.emit(analysis);
    }

    public onResetAnalysis(): void {
        this.drawerService.closeDrawer();
        this.handleResetAnalysis.emit();
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
    }
}
